import { useContext } from "react";
import { ProductFormActionsContext } from "./context";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useFormContext } from "react-hook-form";
import { ProductFormValues } from "./types";
import React from "react";
import TaxGroup from "../../../../utilities/data/model/TaxGroup";
import { MarkupType } from "@towersystems/roam-common/lib/generated-types";
import Category from "../../../../utilities/data/model/Category";
import Department from "../../../../utilities/data/model/Department";
import Product from "../../../../utilities/data/model/Product";
import { Q } from "@nozbe/watermelondb";
import { useTranslation } from "../../../../utilities/i18n";

export const useProductFormActionsContextProvider = () => {
  const context = useContext(ProductFormActionsContext);
  return context;
};

export const useCommonFormActions = () => {
  const { t } = useTranslation("product");
  const database = useDatabase();

  const [existingProductId, setExistingProductId] = React.useState<
    string | undefined
  >(undefined);

  const { formState, setError, clearErrors, setValue, watch, trigger } =
    useFormContext<ProductFormValues>();

  const { errors } = formState;

  const watchedStockNumber = watch("stockNumber");
  const watchedCategoryId = watch("categoryId");
  const watchedDepartmentId = watch("departmentId");
  const watchedTaxGroupId = watch("taxGroupId");
  const watchedProductId = watch("id");

  React.useEffect(() => {
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  React.useEffect(() => {
    clearErrors("root");
    watchedStockNumber &&
      database
        .get<Product>("products")
        .query(Q.where("stock_number", Q.eq(watchedStockNumber)))
        .then((products) => {
          if (products.length && products[0].id !== watchedProductId) {
            setExistingProductId(products[0].id);
            setError("root.stockNumber", {
              type: "manual",
              message: t("error.barcode_exists"),
            });
          } else {
            setExistingProductId(undefined);
          }
        });
  }, [watchedStockNumber, watchedProductId]);

  React.useEffect(() => {
    (async () => {
      try {
        const department = watchedDepartmentId
          ? await database
              .get<Department>("departments")
              .find(watchedDepartmentId)
          : undefined;

        const category = watchedCategoryId
          ? await database.get<Category>("categories").find(watchedCategoryId)
          : undefined;

        let retailMarkup =
          category?.markup?.find((c) => c.markupType == MarkupType.RETAIL) ??
          department?.markup?.find((c) => c.markupType == MarkupType.RETAIL);

        let tradeMarkup =
          category?.markup?.find((c) => c.markupType == MarkupType.TRADE) ??
          department?.markup?.find((c) => c.markupType == MarkupType.TRADE);

        setValue("markupPercent", retailMarkup?.markupPercentage ?? 0);
        setValue("tradeMarkupPercent", tradeMarkup?.markupPercentage ?? 0);
        setValue("tradeMarkupType", tradeMarkup?.markupStyle ?? "");
      } catch (err) {}
    })();
  }, [watchedCategoryId, watchedDepartmentId, setValue]);

  React.useEffect(() => {
    watchedTaxGroupId &&
      database
        .get<TaxGroup>("tax_groups")
        .find(watchedTaxGroupId)
        .then((t) => {
          setValue("taxRatePercent", t.salePercentage);
        })
        .catch((err) => setValue("taxRatePercent", 0));
  }, [watchedTaxGroupId]);

  return {
    existingProductId,
  };
};
